import classNames from 'classnames'
import { memo, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import { useNavigate } from 'react-router-dom'

import type { ReactElement } from 'react'
import type { IDataBuilderConfig, IGetOverviewResponse, IGetSeriesDataBody } from '~/types/graph-builder'

import { useFormattedBody } from './utils'

import AddToFavourites from '~/components/AddToFavourite'
import AnalyticBlockMenu from '~/components/AnalyticBlocksMenu'
import AnalyticGenericBlock from '~/components/AnalyticGenericBlock'
import AnalyticTooltip from '~/components/AnalyticsTooltip'
import AnalyticWarningTooltip from '~/components/AnalyticsWarningTooltip'
import BlockValue from '~/components/Blocks/Value'
import DropdownMenu from '~/components/DropdownMenu'
import Variation, { getDirection } from '~/components/Variation'
import { AUTHORIZATIONS, dataNotAvailableText } from '~/const'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useGetBuilderBatchDataQuery } from '~/queries/graphs-builder'
import { Mode } from '~/types/graph-builder'
import { isIntranet } from '~/utils/env'
import { formatGraphTitle, formatValue } from '~/utils/graphs-builder'
import { copyText } from '~/utils/navigator'

import './styles.scss'

export interface Props {
  apiSelectionFn?: (body: IGetSeriesDataBody[]) => Promise<any>
  appKey: string
  canBeFavourite?: boolean
  className?: string
  config: IDataBuilderConfig
  deviceType: string
  isAppLoading: boolean
  kpiField: string
  lazy?: boolean
  withDefaultInstallationCountry?: boolean
}

const DynamicDataBlock = ({
  apiSelectionFn,
  appKey,
  canBeFavourite = true,
  className,
  config,
  deviceType,
  isAppLoading,
  kpiField,
  lazy = true,
  withDefaultInstallationCountry = true,
}: Props): ReactElement => {
  /** Local state */

  const userAuthorizedToEdit = useIsUserAuthorized([AUTHORIZATIONS.ANALYTICS.GRAPH_BUILDER.WRITE])

  const navigate = useNavigate()

  const { inView, ref } = useInView({ triggerOnce: true })

  const genericBlockClasses = classNames('dynamic-data-block-container', className)

  const title = useMemo(() => formatGraphTitle(config.title), [config.title])

  const body = useFormattedBody({ appKey, config, deviceType, kpiField, withDefaultInstallationCountry })

  const isSomeValues = useMemo(() => body.some((el) => el.kpi_values.values.length > 0), [body])

  const isQueryEnabled = useMemo(() => {
    if (isSomeValues && config.series.length > 0 && config.series.some((el) => el.values.length > 0)) {
      return lazy && config.id ? inView : true
    }
    return false
  }, [config.id, config.series, inView, isSomeValues, lazy])

  const menu = useMemo(
    () => [
      ...(userAuthorizedToEdit
        ? [
            {
              items: [
                {
                  label: 'Edit',
                  onClick: () => navigate(`/analytics/ca/builder/${config.id}`),
                },
              ],
            },
          ]
        : []),
      {
        items: [
          {
            label: 'Copy link',
            // NOTE: remove the previous hash from the url to not duplicate it
            onClick: () => copyText(`${window.location.href.split('#')[0]}#${config.id}`, 'Url copied to clipboard!'),
          },
        ],
      },
    ],
    [config.id, navigate, userAuthorizedToEdit]
  )

  /** Queries & Mutations */

  const queryParams = {
    body,
    options: {
      enabled: isQueryEnabled,
    },
    queryFn: apiSelectionFn,
  }

  const {
    data: series,
    isError,
    isLoading: isDataLoading,
  } = useGetBuilderBatchDataQuery<IGetOverviewResponse[]>({
    ...queryParams,
    mode: Mode.OVERVIEW,
  })

  /** Handlers */

  const renderChildren = () => {
    if (isError) return <div className="no-data-container">API error</div>

    if (!isSomeValues) return <span>Please select some values.</span>

    if (!(series && series?.[0]?.data?.[0]?.data?.base)) {
      return <div className="no-data-container">{dataNotAvailableText}</div>
    }

    const { data } = series?.[0]?.data?.[0]

    return (
      <>
        <BlockValue className="data-container">{data.base ? formatValue(config.yAxes[0], data.base) : 0}</BlockValue>
        {data.progress ? (
          <Variation
            direction={getDirection(data.progress)}
            reversed={config.wantNegativeEvolution}
            value={data.progress * 100}
          />
        ) : null}
      </>
    )
  }

  return (
    <>
      {/**
       * This id is used as an anchor.
       * The `useScrollToElement` in the generic `Layout` component will detect if an anchor is present
       * in the url and scroll to it if the anchor is present in the view.
       */}
      {config.id ? <div id={config.id} ref={ref} /> : null}
      <AnalyticGenericBlock
        className={genericBlockClasses}
        isDraggable={false}
        isLoading={isAppLoading || isDataLoading}
        title={title}
      >
        <AnalyticBlockMenu>
          {canBeFavourite && isIntranet() && config.id ? <AddToFavourites configId={config.id} /> : null}
          {config.warningMessage ? <AnalyticWarningTooltip message={config.warningMessage} /> : null}
          {config.tooltip?.how || config.tooltip?.what || config.tooltip?.relatedGlossaryItems?.length > 0 ? (
            <AnalyticTooltip value={{ ...config.tooltip, glossarySearch: config.tooltip.relatedGlossaryItems }} />
          ) : null}
          <DropdownMenu menu={menu} />
        </AnalyticBlockMenu>
        {renderChildren()}
      </AnalyticGenericBlock>
    </>
  )
}

export default memo(DynamicDataBlock)
